/**
 * ASEED API client.
 *
 * Responsibilities that belong here and nowhere else:
 *
 *  - attach the CSRF token and `credentials: "include"` so the session cookie
 *    is used, never a token in a URL or in localStorage;
 *  - generate and attach an `Idempotency-Key` for every mutation, and REUSE it
 *    across retries of the same logical action so a double tap cannot create a
 *    second charge;
 *  - decode the single error envelope into a typed error rather than letting
 *    callers branch on status codes;
 *  - refuse to send an amount. There is no API in this module that accepts one.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message_key: string;
    details?: Record<string, unknown>;
    trace_id: string;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly messageKey: string;
  readonly details: Record<string, unknown>;
  readonly traceId: string;

  constructor(status: number, body: ApiErrorBody) {
    super(`${body.error.code} (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.error.code;
    this.messageKey = body.error.message_key;
    this.details = body.error.details ?? {};
    this.traceId = body.error.trace_id;
  }

  /** True when retrying the identical request with the same key is safe. */
  get isRetryable(): boolean {
    return this.status === 429 || this.status === 503;
  }

  /** True when the local view of the resource is stale and must be re-read. */
  get requiresResync(): boolean {
    return this.code === "stale_version" || this.status === 409;
  }
}

export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("network request failed");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export interface ClientOptions {
  baseUrl?: string;
  /** Reads the CSRF token the server issued with the session. */
  getCsrfToken?: () => string | null;
  /** Native clients only. Browsers must return null and rely on the cookie. */
  getBearerToken?: () => string | null;
  fetchImpl?: typeof fetch;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  /**
   * Stable key for this logical action. Supply the SAME value when retrying
   * so the server replays its stored result instead of charging twice.
   * Required for every mutation; ignored for GET.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

const MUTATIONS: ReadonlySet<Method> = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function newIdempotencyKey(): string {
  // crypto.randomUUID is available in every browser this app supports and in
  // Node 19+. There is no Math.random fallback: a weak key risks a collision
  // across principals, which the server would treat as a replay.
  return crypto.randomUUID();
}

export class AseedApiClient {
  private readonly baseUrl: string;
  private readonly getCsrfToken: () => string | null;
  private readonly getBearerToken: () => string | null;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? "/api/v1";
    this.getCsrfToken = options.getCsrfToken ?? (() => null);
    this.getBearerToken = options.getBearerToken ?? (() => null);
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(
    method: Method,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    if (MUTATIONS.has(method) && !options.idempotencyKey) {
      // Throwing beats silently generating one here: a key generated per call
      // site would differ on retry and defeat the whole mechanism.
      throw new Error(
        `${method} ${path} requires an idempotencyKey. Create it once per logical ` +
          `action with newIdempotencyKey() and reuse it across retries.`,
      );
    }

    const url = new URL(this.baseUrl + path, globalThis.location?.origin ?? "http://localhost");
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }

    const headers: Record<string, string> = { Accept: "application/json" };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;

    const csrf = this.getCsrfToken();
    if (csrf && MUTATIONS.has(method)) headers["X-CSRF-Token"] = csrf;

    const bearer = this.getBearerToken();
    if (bearer) headers["Authorization"] = `Bearer ${bearer}`;

    let response: Response;
    try {
      response = await this.fetchImpl(url.toString(), {
        method,
        headers,
        // First-party cookie. The token is never in the URL or in storage.
        credentials: "include",
        body: body === undefined ? null : JSON.stringify(body),
        signal: options.signal ?? null,
      });
    } catch (cause) {
      // A network failure is NOT a failed payment. Callers must re-read state
      // rather than assume the mutation did not happen.
      throw new NetworkError(cause);
    }

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      throw new ApiError(response.status, {
        error: {
          code: "non_json_response",
          message_key: "error.internal",
          details: { content_type: contentType },
          trace_id: response.headers.get("x-request-id") ?? "unset",
        },
      });
    }

    const payload = (await response.json()) as unknown;

    if (!response.ok) {
      throw new ApiError(response.status, payload as ApiErrorBody);
    }
    return payload as T;
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("GET", path, undefined, options);
  }

  post<T>(path: string, body: unknown, options: RequestOptions): Promise<T> {
    return this.request<T>("POST", path, body, options);
  }

  patch<T>(path: string, body: unknown, options: RequestOptions): Promise<T> {
    return this.request<T>("PATCH", path, body, options);
  }

  delete<T>(path: string, options: RequestOptions): Promise<T> {
    return this.request<T>("DELETE", path, undefined, options);
  }
}
